import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid, type KstDataGridColumn, type KstDataGridRows } from '@/components/KstDataGrid';
import { AdminTabBar } from '@/components/AdminTabBar';
import { ConfirmModal } from '@/components/ConfirmModal';
import { AddStandardNocModal, type NocOption } from '@/components/modals/AddStandardNocModal';
import { AddCustomNocModal, type NocGroupOption } from '@/components/modals/AddCustomNocModal';
import { AddSelfInitiateNocModal, type SelfInitiateNocOption, type SelfInitiateNocEditData } from '@/components/modals/AddSelfInitiateNocModal';
import { ViewSopModal } from '@/components/modals/ViewSopModal';
import { EditSopModal } from '@/components/modals/EditSopModal';
import { EditPriorityModal } from '@/components/modals/EditPriorityModal';
import type { NatureOfCallData } from '@/services/api-types';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { hasValue } from '@/utils/legacy-utils';

import { getCurrentCompanyId } from '@/utils/auth';

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const deepCopyProperties = _wg('deepCopyProperties');
const sortNatureOfCallList = _wg('sortNatureOfCallList');
const loadNocSopImages = _wg('loadNocSopImages');

const tabs = [
  { id: 'showManageNocTab', label: 'NOC' },
  { id: 'showCustomizeSelfInitiateTab', label: 'Self Initiate' },
];

const nocManagementColumns: KstDataGridColumn[] = [
  { field: 'code', name: 'Code', size: 'hidden' },
  {
    field: 'value',
    name: 'Nature of Call',
    size: 'col-xs-3',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.value)) {
        const style = e.isEnabled === false ? 'opacity: 0.5;' : '';
        return `<span style="${style}">${e.value}</span>`;
      }
      return 'Error getting Nature of Call.';
    },
  },
  {
    field: 'gValue',
    name: 'Group',
    size: 'col-xs-2',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.gCode)) {
        const style = e.isEnabled === false ? 'opacity: 0.5;' : '';
        return `<span style="${style}">${e.gValue}</span>`;
      }
      return 'Error getting group.';
    },
  },
  {
    field: 'sgValue',
    name: 'Subgroup',
    size: 'col-xs-2',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.sgCode)) {
        const style = e.isEnabled === false ? 'opacity: 0.5;' : '';
        return `<span style="${style}">${e.sgValue}</span>`;
      }
      return 'Error getting subgroup.';
    },
  },
  {
    field: 'sop',
    name: 'SOP',
    size: 'col-xs-1',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.sop)) {
        return `<a class="kst-sop-view-link" data-noc-code="${e.code}">View</a>`;
      }
      return '';
    },
  },
  {
    field: 'priority',
    name: 'Priority',
    size: 'col-xs-1',
    template: (e: any) => {
      const style = e && e.isEnabled === false ? 'opacity: 0.5;' : '';
      const val = (e && e.priority !== null && e.priority !== undefined) ? e.priority : '\u2014';
      return `<span style="${style}">${val}</span>`;
    },
  },
];

const nocManagementHeaderButtons = [
  { name: 'Add Standard', function: 'addstandard', src: '/Content/TourImages/add-r.svg' },
  { name: 'Add Custom', function: 'addcustom', src: '/Content/TourImages/add-r.svg' },
  { name: 'Edit SOP', function: 'editsop', src: '/Content/TourImages/pen.svg' },
  { name: 'Edit Priority', function: 'editpriority', src: '/Content/TourImages/pen.svg' },
  { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
];

const OFFICER_STATUS_LEVEL_LABELS: Record<string, string> = {
  DISPATCHED: 'Dispatched',
  ENROUTE: 'Enroute',
  ON_SCENE: 'On Scene',
  CLEARED: 'Cleared',
};

const selfInitiateColumns: KstDataGridColumn[] = [
  { field: 'code', name: 'Code', size: 'hidden' },
  {
    field: 'value',
    name: 'Nature of Call',
    size: 'col-xs-3',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.value)) {
        return e.value;
      }
      return 'Error getting Nature of Call';
    },
  },
  {
    field: 'isShownOnMobile',
    name: 'Small Screens?',
    size: 'col-xs-2',
    template: (e: any) => {
      return e.isShownOnMobile ? 'Yes' : '';
    },
  },
  {
    field: 'officerStatusLevel',
    name: 'Status',
    size: 'col-xs-2',
    template: (e: any) => {
      return OFFICER_STATUS_LEVEL_LABELS[e.officerStatusLevel] || e.officerStatusLevel || 'On Scene';
    },
  },
  {
    field: 'secondaryNocValue',
    name: 'Secondary NOC',
    size: 'col-xs-2',
    template: (e: any) => {
      return e.secondaryNocValue || '';
    },
  },
];

const selfInitiateHeaderButtons = [
  { name: 'Add', function: 'add', src: '/Content/TourImages/add-r.svg' },
  { name: 'Edit', function: 'edit', src: '/Content/TourImages/pen.svg' },
  { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
];

export function NocManagementScreen() {
  const { theme } = useTheme();

  const [activeTab, setActiveTab] = useState('showManageNocTab');
  const [activePage, setActivePage] = useState('showManageNoc');

  // NOC management entries
  const [nocEntries, setNocEntries] = useState<NatureOfCallData[] | null>(null);
  const [selfInitiateEntries, setSelfInitiateEntries] = useState<any[] | null>(null);
  const [isNocLoading, setIsNocLoading] = useState<boolean>(false);
  const [isSelfInitiateLoading, setIsSelfInitiateLoading] = useState<boolean>(false);

  // Caches
  const natureOfCallListCacheRef = useRef<any[] | null>(null);
  const globalNatureOfCallListCacheRef = useRef<any[] | null>(null);
  const groupAndSubgroupCacheRef = useRef<any | null>(null);
  const selfInitiateNocListCacheRef = useRef<any[] | null>(null);

  // Available NOCs for self-initiate add dialog
  const [dialogSelfInitiateNocList, setDialogSelfInitiateNocList] = useState<any[] | null>(null);
  const [allCompanyNocsForModal, setAllCompanyNocsForModal] = useState<any[]>([]);

  // Add Standard NOC modal
  const [addStandardNocModalOpen, setAddStandardNocModalOpen] = useState(false);
  const [addStandardNocLoading, setAddStandardNocLoading] = useState(false);
  const [availableStandardNocs, setAvailableStandardNocs] = useState<NocOption[]>([]);

  // Add Custom NOC modal
  const [addCustomNocModalOpen, setAddCustomNocModalOpen] = useState(false);
  const [nocGroupList, setNocGroupList] = useState<NocGroupOption[]>([]);
  const [nocSubgroupList, setNocSubgroupList] = useState<NocGroupOption[]>([]);

  // Add/Edit Self Initiate NOC modal
  const [addSelfInitiateNocModalOpen, setAddSelfInitiateNocModalOpen] = useState(false);
  const [availableSelfInitiateNocs, setAvailableSelfInitiateNocs] = useState<SelfInitiateNocOption[]>([]);
  const [editSelfInitiateData, setEditSelfInitiateData] = useState<SelfInitiateNocEditData | null>(null);

  // Delete NOC confirm modal
  const [deleteNocConfirmOpen, setDeleteNocConfirmOpen] = useState(false);
  const [deleteNocConfirmItems, setDeleteNocConfirmItems] = useState<any[]>([]);
  const [deleteNocConfirmMessage, setDeleteNocConfirmMessage] = useState('');

  // Delete Self Initiate confirm modal
  const [deleteSelfInitiateConfirmOpen, setDeleteSelfInitiateConfirmOpen] = useState(false);
  const [deleteSelfInitiateConfirmItems, setDeleteSelfInitiateConfirmItems] = useState<any[]>([]);
  const [deleteSelfInitiateConfirmMessage, setDeleteSelfInitiateConfirmMessage] = useState('');
  const [isDeletingSelfInitiate, setIsDeletingSelfInitiate] = useState<boolean>(false);
  const [isSelfInitiateSaving, setIsSelfInitiateSaving] = useState<boolean>(false);

  // View SOP modal
  const [viewSopModalOpen, setViewSopModalOpen] = useState(false);
  const [viewSopNocValue, setViewSopNocValue] = useState('');
  const [viewSopContent, setViewSopContent] = useState('');

  // Edit SOP modal
  const [editSopModalOpen, setEditSopModalOpen] = useState(false);
  const [editSopNocCode, setEditSopNocCode] = useState<number | null>(null);
  const [editSopNocValue, setEditSopNocValue] = useState('');
  const [editSopContent, setEditSopContent] = useState('');

  // Edit Priority modal
  const [editPriorityModalOpen, setEditPriorityModalOpen] = useState(false);
  const [editPriorityNoc, setEditPriorityNoc] = useState<NatureOfCallData | null>(null);

  // Grid ref for scroll
  const nocGridContainerRef = useRef<HTMLDivElement>(null);

  // Stable ref to nocEntries so callbacks don't go stale
  const nocEntriesRef = useRef(nocEntries);
  nocEntriesRef.current = nocEntries;

  const nocRows: KstDataGridRows = useMemo(() => ({ entries: nocEntries }), [nocEntries]);
  const selfInitiateRows: KstDataGridRows = useMemo(() => ({ entries: selfInitiateEntries }), [selfInitiateEntries]);

  // Cache helpers
  const getCompanyNatureOfCallList = useCallback((): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      if (hasValue(natureOfCallListCacheRef.current)) {
        resolve(deepCopyProperties(natureOfCallListCacheRef.current));
      } else {
        api.listCompanyNatureOfCalls(
          (result: any) => {
            natureOfCallListCacheRef.current = result;
            resolve(deepCopyProperties(result));
          },
          (errorResult: any) => {
            reject(errorResult);
            App.showPopupNotification('Error getting nature of call data.', 'error');
          }
        );
      }
    });
  }, []);

  const getGlobalNatureOfCallList = useCallback((): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      if (hasValue(globalNatureOfCallListCacheRef.current)) {
        resolve(deepCopyProperties(globalNatureOfCallListCacheRef.current));
      } else {
        api.listGlobalNatureOfCalls(
          (result: any) => {
            globalNatureOfCallListCacheRef.current = result;
            resolve(deepCopyProperties(result));
          },
          (errorResult: any) => {
            reject(errorResult);
            App.showPopupNotification('Error getting standard nature of call data.', 'error');
          }
        );
      }
    });
  }, []);

  const getGroupAndSubgroupCodes = useCallback((): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (hasValue(groupAndSubgroupCacheRef.current)) {
        resolve(deepCopyProperties(groupAndSubgroupCacheRef.current));
      } else {
        api.getGroupAndSubgroupNocCodes(
          (result: any) => {
            groupAndSubgroupCacheRef.current = result;
            resolve(deepCopyProperties(result));
          },
          (errorResult: any) => {
            reject(errorResult);
            App.showPopupNotification('Error getting nature of call group/subgroup data.', 'error');
          }
        );
      }
    });
  }, []);

  const getSelfInitiateNocList = useCallback((): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      if (hasValue(selfInitiateNocListCacheRef.current)) {
        resolve(deepCopyProperties(selfInitiateNocListCacheRef.current));
      } else {
        api.getSelfInitiateNocList(
          (result: any) => {
            selfInitiateNocListCacheRef.current = result;
            resolve(deepCopyProperties(result));
          },
          (failResult: any) => {
            App.showPopupNotification('Error getting self initiate list.', 'error');
            reject(failResult);
          }
        );
      }
    });
  }, []);

  // Scroll-to-bottom: use a ref flag + useEffect so the scroll happens
  // after React has committed the new grid rows to the DOM.
  const shouldScrollToBottomRef = useRef(false);

  useEffect(() => {
    if (shouldScrollToBottomRef.current) {
      shouldScrollToBottomRef.current = false;
      const scrollEl = nocGridContainerRef.current?.closest('.kst-main-content');
      if (scrollEl) {
        scrollEl.scrollTop = scrollEl.scrollHeight;
      }
    }
  }, [nocEntries]);

  const updateInitialRowOrder = useCallback((values: any[]) => {
    sortNatureOfCallList(values);
    for (let i = 0; i < values.length; i++) {
      values[i].listIdx = i + 1;
      values[i].isListEnabled = true;
      values[i].id = values[i].code;
    }
  }, []);

  const refreshManageNocList = useCallback(
    (doScrollToBottom = false) => {
      setIsNocLoading(true);
      Promise.all([getCompanyNatureOfCallList(), getGroupAndSubgroupCodes()]).then((values) => {
        const entries = values[0] as any[];
        updateInitialRowOrder(entries);

        const codes = values[1] as any;
        const nocGroupList = codes.groupCodes;
        const nocSubgroupList = codes.subgroupCodes;
        const nocGCodeToGValue: Record<string, string> = nocGroupList.reduce((result: any, item: any) => {
          result[item.code.toString()] = item.value;
          return result;
        }, {});
        const nocSgCodeToSgValue: Record<string, string> = nocSubgroupList.reduce((result: any, item: any) => {
          result[item.code.toString()] = item.value;
          return result;
        }, {});

        for (const entry of entries) {
          if (hasValue(entry.gCode)) {
            entry.gValue = nocGCodeToGValue[entry.gCode.toString()];
          }
          if (hasValue(entry.sgCode)) {
            entry.sgValue = nocSgCodeToSgValue[entry.sgCode.toString()];
          }
        }

        if (doScrollToBottom) {
          shouldScrollToBottomRef.current = true;
        }
        setNocEntries(entries);
        setIsNocLoading(false);
      }).catch(() => {
        setIsNocLoading(false);
      });
    },
    [getCompanyNatureOfCallList, getGroupAndSubgroupCodes, updateInitialRowOrder]
  );

  const refreshSelfInitiateList = useCallback(
    (clearCache = false) => {
      if (clearCache) {
        selfInitiateNocListCacheRef.current = null;
      }

      setIsSelfInitiateLoading(true);
      Promise.all([getCompanyNatureOfCallList(), getSelfInitiateNocList()]).then(
        (values) => {
          const selfInitiateValues = values[1] as Array<{ code: number; value: string; isShownOnMobile: boolean; officerStatusLevel: string; secondaryNoc: number | null }>;

          // Resolve secondary NOC values from company NOC list
          const natureOfCallList = values[0] as any[];
          const nocCodeToValue: Record<string, string> = {};
          for (const noc of natureOfCallList) {
            nocCodeToValue[noc.code.toString()] = noc.value;
          }
          for (const entry of selfInitiateValues) {
            (entry as any).secondaryNocValue = entry.secondaryNoc ? (nocCodeToValue[entry.secondaryNoc.toString()] || `Code ${entry.secondaryNoc}`) : '';
          }

          setSelfInitiateEntries(selfInitiateValues);

          // Build NOC list for adding more (exclude current self-initiate codes)
          const selfInitiateCodeToTrue: Record<string, boolean> = {};
          for (const value of selfInitiateValues) {
            selfInitiateCodeToTrue[value.code.toString()] = true;
          }

          const filtered = natureOfCallList.filter((item) => !selfInitiateCodeToTrue[item.code.toString()]);
          setDialogSelfInitiateNocList(filtered);
          setAllCompanyNocsForModal(natureOfCallList);
          setIsSelfInitiateLoading(false);
        },
        () => {
          // Errors handled by the specific promises.
          setIsSelfInitiateLoading(false);
        }
      );
    },
    [getCompanyNatureOfCallList, getSelfInitiateNocList]
  );

  const updateNocs = useCallback(
    (entries: any[], doScrollToBottom = false, skipRefresh = false) => {
      const nocs = entries.map((elt: any) => ({ code: elt.code, listIdx: elt.listIdx }));
      api.updateCompanyNatureOfCallList(
        { natureOfCallList: nocs },
        () => {
          natureOfCallListCacheRef.current = null;
          if (!skipRefresh) {
            refreshManageNocList(doScrollToBottom);
          }
        },
        () => {
          App.showPopupNotification('Failed to update nature of calls.', 'error');
        }
      );
    },
    [refreshManageNocList]
  );

  // View SOP via cell click
  const viewSop = useCallback(
    (nocCode: number) => {
      const row = (nocEntriesRef.current ?? []).find((e: any) => e.code === nocCode);
      if (!row) return;

      setViewSopNocValue(row.value);
      setViewSopContent('');
      setViewSopModalOpen(true);

      loadNocSopImages(row.sop || '', nocCode, getCurrentCompanyId(), (processedSop: string) => {
        setViewSopContent(processedSop);
      });
    },
    []
  );

  // Load initial data
  useEffect(() => {
    refreshManageNocList();
    refreshSelfInitiateList();
  }, []);

  // Handle SOP view link clicks via event delegation
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('kst-sop-view-link')) {
        e.preventDefault();
        const nocCode = parseInt(target.getAttribute('data-noc-code') || '0', 10);
        if (nocCode) {
          viewSop(nocCode);
        }
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [viewSop]);

  // Tab change
  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
      if (tabId === 'showManageNocTab') {
        setActivePage('showManageNoc');
      } else if (tabId === 'showCustomizeSelfInitiateTab') {
        setActivePage('showCustomizeSelfInitiate');
        refreshSelfInitiateList();
      }
    },
    [refreshSelfInitiateList]
  );

  // NOC grid button commands
  const handleNocButtonCommand = useCallback(
    (functionName: string, checkedItems: any[]) => {
      if (functionName === 'addstandard') {
        setAddStandardNocModalOpen(true);
        setAddStandardNocLoading(true);
        Promise.all([getCompanyNatureOfCallList(), getGlobalNatureOfCallList()]).then((values) => {
          const companyNocs = values[0] as any[];
          const globalNocs = values[1] as any[];
          const codeMap: Record<string, boolean> = companyNocs.reduce((result: any, item: any) => {
            result[item.code] = true;
            return result;
          }, {});
          const available = globalNocs.filter((noc: any) => !codeMap[noc.code.toString()]);
          setAvailableStandardNocs(available);
          setAddStandardNocLoading(false);
        }).catch(() => {
          setAddStandardNocLoading(false);
        });
      } else if (functionName === 'addcustom') {
        getGroupAndSubgroupCodes().then((codes: any) => {
          setNocGroupList(codes.groupCodes);
          setNocSubgroupList(codes.subgroupCodes);
          setAddCustomNocModalOpen(true);
        });
      } else if (functionName === 'editsop') {
        if (checkedItems.length === 0) {
          App.showPopupNotification('Please select the nature of call to edit by clicking the checkbox.', 'warning');
          return;
        } else if (checkedItems.length > 1) {
          App.showPopupNotification('Only one nature of call may be edited at a time.', 'warning');
          return;
        }
        const checkedItem = checkedItems[0];
        setEditSopNocCode(checkedItem.code);
        setEditSopNocValue(checkedItem.value);
        setEditSopContent(checkedItem.sop || '');
        setEditSopModalOpen(true);
      } else if (functionName === 'editpriority') {
        if (checkedItems.length === 0) {
          App.showPopupNotification('Please select the nature of call to edit by clicking the checkbox.', 'warning');
          return;
        } else if (checkedItems.length > 1) {
          App.showPopupNotification('Only one nature of call may be edited at a time.', 'warning');
          return;
        }
        setEditPriorityNoc(checkedItems[0] as NatureOfCallData);
        setEditPriorityModalOpen(true);
      } else if (functionName === 'delete') {
        if (checkedItems.length === 0) {
          App.showPopupNotification('NOC must be selected to delete.', 'warning');
          return;
        }
        const count = checkedItems.length;
        const message =
          count === 1
            ? `Are you sure you want to delete "${checkedItems[0].value}"?`
            : `Are you sure you want to delete ${count} nature of calls?`;
        setDeleteNocConfirmItems(checkedItems);
        setDeleteNocConfirmMessage(message);
        setDeleteNocConfirmOpen(true);
      }
    },
    [getCompanyNatureOfCallList, getGlobalNatureOfCallList, getGroupAndSubgroupCodes]
  );

  // Self-initiate grid button commands
  const handleSelfInitiateButtonCommand = useCallback(
    (functionName: string, checkedItems: any[]) => {
      if (functionName === 'add') {
        setEditSelfInitiateData(null);
        setAvailableSelfInitiateNocs(dialogSelfInitiateNocList || []);
        setAddSelfInitiateNocModalOpen(true);
      } else if (functionName === 'edit') {
        if (checkedItems.length === 0) {
          App.showPopupNotification('Please select the nature of call to edit by clicking the checkbox.', 'warning');
          return;
        } else if (checkedItems.length > 1) {
          App.showPopupNotification('Only one nature of call may be edited at a time.', 'warning');
          return;
        }
        const item = checkedItems[0];
        setEditSelfInitiateData({
          nocCode: item.code,
          nocValue: item.value,
          isShownOnMobile: item.isShownOnMobile,
          officerStatusLevel: item.officerStatusLevel || 'ON_SCENE',
          secondaryNoc: item.secondaryNoc || null,
        });
        setAddSelfInitiateNocModalOpen(true);
      } else if (functionName === 'delete') {
        if (checkedItems.length === 0) {
          App.showPopupNotification('Please select the nature of call to remove by clicking the checkbox.', 'warning');
          return;
        }
        const count = checkedItems.length;
        const message =
          count === 1
            ? `Are you sure you want to remove "${checkedItems[0].value}" from Self Initiate?`
            : `Are you sure you want to remove ${count} nature of calls from Self Initiate?`;
        setDeleteSelfInitiateConfirmItems(checkedItems);
        setDeleteSelfInitiateConfirmMessage(message);
        setDeleteSelfInitiateConfirmOpen(true);
      }
    },
    [dialogSelfInitiateNocList]
  );

  // Add Standard NOC save
  const handleAddStandardNocSave = useCallback(
    (data: { nocCodes: number[] }) => {
      const updated = [...(nocEntriesRef.current ?? [])];
      for (const nocCode of data.nocCodes) {
        updated.push({ code: nocCode, listIdx: null });
      }
      for (let i = 0; i < updated.length; i++) {
        updated[i].listIdx = i + 1;
      }
      setIsNocLoading(true);
      updateNocs(updated, true);
      setAddStandardNocModalOpen(false);
      setAddStandardNocLoading(false);
    },
    [updateNocs]
  );

  // Add Custom NOC save
  const handleAddCustomNocSave = useCallback(
    (data: { value: string; groupCode: number; subgroupCode: number; priority: number | null }) => {
      const nocData = {
        value: data.value,
        gCode: data.groupCode,
        sgCode: data.subgroupCode,
        sop: null,
        priority: data.priority,
      };
      api.addCustomNatureOfCall(
        nocData,
        () => {
          natureOfCallListCacheRef.current = null;
          refreshManageNocList(true);
          setAddCustomNocModalOpen(false);
        },
        () => {
          App.showPopupNotification('Failed to save nature of call.', 'error');
        }
      );
    },
    [refreshManageNocList]
  );

  // Edit SOP save
  const handleEditSopSave = useCallback(
    (data: { nocCode: number | null; sop: string; attachmentInfoList: any[] }) => {
      const updateData = {
        natureOfCallCode: data.nocCode,
        companyId: getCurrentCompanyId(),
        sop: data.sop,
        attachmentInfoList: data.attachmentInfoList,
      };
      api.updateCompanyNatureOfCall(
        updateData,
        () => {
          natureOfCallListCacheRef.current = null;
          refreshManageNocList();
          setEditSopModalOpen(false);
        },
        () => {
          App.showPopupNotification('Failed to update SOP for nature of call.', 'error');
        }
      );
    },
    [refreshManageNocList]
  );

  // Delete NOCs confirm
  const handleConfirmDeleteNocs = useCallback(() => {
    const codeMap: Record<string, boolean> = {};
    deleteNocConfirmItems.forEach((item: any) => {
      codeMap[item.code] = true;
    });
    const remaining = (nocEntriesRef.current ?? []).filter((entry: any) => !codeMap[entry.code.toString()]);
    for (let i = 0; i < remaining.length; i++) {
      remaining[i].listIdx = i + 1;
    }
    setNocEntries(remaining);
    selfInitiateNocListCacheRef.current = null;
    updateNocs(remaining);
    setDeleteNocConfirmOpen(false);
    setDeleteNocConfirmItems([]);
    setDeleteNocConfirmMessage('');
  }, [deleteNocConfirmItems, updateNocs]);

  const handleCancelDeleteNocs = useCallback(() => {
    setDeleteNocConfirmOpen(false);
    setDeleteNocConfirmItems([]);
    setDeleteNocConfirmMessage('');
  }, []);

  // Add/Edit Self Initiate NOC save
  const handleAddSelfInitiateNocSave = useCallback(
    (data: { nocCode: number; isShownOnMobile: boolean; officerStatusLevel: string; secondaryNoc: number | null }) => {
      setIsSelfInitiateSaving(true);
      const isEdit = editSelfInitiateData != null;
      const onSuccess = () => {
        setIsSelfInitiateSaving(false);
        setAddSelfInitiateNocModalOpen(false);
        setEditSelfInitiateData(null);
        refreshSelfInitiateList(true);
      };
      const onError = () => {
        setIsSelfInitiateSaving(false);
        App.showPopupNotification(isEdit ? 'Error updating self initiate NOC.' : 'Error adding self initiate NOC.', 'error');
      };
      if (isEdit) {
        api.updateSelfInitiateNoc(data.nocCode, data.isShownOnMobile, data.officerStatusLevel, data.secondaryNoc, onSuccess, onError);
      } else {
        api.addSelfInitiateNoc(data.nocCode, data.isShownOnMobile, data.officerStatusLevel, data.secondaryNoc, onSuccess, onError);
      }
    },
    [refreshSelfInitiateList, editSelfInitiateData]
  );

  // Delete Self Initiate NOCs confirm
  const handleConfirmDeleteSelfInitiateNocs = useCallback(() => {
    const checkedItems = deleteSelfInitiateConfirmItems;
    let count = 0;
    const len = checkedItems.length;
    setIsDeletingSelfInitiate(true);
    for (let i = 0; i < len; i++) {
      api.deleteSelfInitiateNoc(
        checkedItems[i].code,
        () => {
          count++;
          if (count === len) {
            setIsDeletingSelfInitiate(false);
            refreshSelfInitiateList(true);
          }
        },
        () => {
          setIsDeletingSelfInitiate(false);
          App.showPopupNotification('Error deleting self initiate NOC.', 'error');
        }
      );
    }
    setDeleteSelfInitiateConfirmOpen(false);
    setDeleteSelfInitiateConfirmItems([]);
    setDeleteSelfInitiateConfirmMessage('');
  }, [deleteSelfInitiateConfirmItems, refreshSelfInitiateList]);

  const handleCancelDeleteSelfInitiateNocs = useCallback(() => {
    setDeleteSelfInitiateConfirmOpen(false);
    setDeleteSelfInitiateConfirmItems([]);
    setDeleteSelfInitiateConfirmMessage('');
  }, []);

  // NOC row moved
  const handleNocRowMoved = useCallback(
    (row: any) => {
      const values = [...(nocEntriesRef.current ?? [])];
      const idx = values.findIndex((v: any) => v.code === row.code);
      if (idx >= 0) {
        values.splice(idx, 1);
      }
      values.splice(row.listIdx - 1, 0, row);
      for (let i = 0; i < values.length; i++) {
        values[i].listIdx = i + 1;
      }
      setNocEntries(values);
      updateNocs(values, false, true);
    },
    [updateNocs]
  );

  // NOC status toggle (enabled/disabled)
  const handleStatusSwitch = useCallback(
    (nocCode: any, newEnabledState: boolean) => {
      api.setNatureOfCallEnabled(
        nocCode,
        newEnabledState,
        () => {
          natureOfCallListCacheRef.current = null;
          refreshManageNocList();
        },
        (failResult: any) => {
          // Revert the toggle
          setNocEntries((prev) =>
            (prev ?? []).map((entry) =>
              entry.code === nocCode ? { ...entry, isEnabled: !newEnabledState } : entry
            )
          );
          if (failResult?.responseJSON?.errors) {
            const errors = failResult.responseJSON.errors;
            if (errors.some((e: any) => e.code === 'com.kerkton.orna.command.SetNatureOfCallEnabledCommand.lastEnabled')) {
              App.showPopupNotification('At least one Nature of Call must remain enabled.', 'warning');
              return;
            }
          }
          App.showPopupNotification('Failed to update Nature of Call enabled state.', 'error');
        }
      );
    },
    [refreshManageNocList]
  );

  return (
    <div className={theme}>
      {/* Modals */}
      <AddStandardNocModal
        open={addStandardNocModalOpen}
        availableNocs={availableStandardNocs}
        loading={addStandardNocLoading}
        theme={theme}
        onSave={handleAddStandardNocSave}
        onCancel={() => setAddStandardNocModalOpen(false)}
      />

      {/* Add Custom NOC modal */}
      <AddCustomNocModal
        open={addCustomNocModalOpen}
        groupList={nocGroupList}
        subgroupList={nocSubgroupList}
        theme={theme}
        onSave={handleAddCustomNocSave}
        onCancel={() => setAddCustomNocModalOpen(false)}
      />

      {/* Add/Edit Self Initiate NOC modal */}
      <AddSelfInitiateNocModal
        open={addSelfInitiateNocModalOpen}
        availableNocs={availableSelfInitiateNocs}
        allCompanyNocs={allCompanyNocsForModal}
        theme={theme}
        saving={isSelfInitiateSaving}
        editData={editSelfInitiateData}
        onSave={handleAddSelfInitiateNocSave}
        onCancel={() => { setAddSelfInitiateNocModalOpen(false); setEditSelfInitiateData(null); }}
      />

      {/* View SOP modal */}
      <ViewSopModal
        open={viewSopModalOpen}
        nocValue={viewSopNocValue}
        sopContent={viewSopContent}
        theme={theme}
        onClose={() => setViewSopModalOpen(false)}
      />

      {/* Edit SOP modal */}
      <EditSopModal
        open={editSopModalOpen}
        nocCode={editSopNocCode}
        nocValue={editSopNocValue}
        sopContent={editSopContent}
        theme={theme}
        onSave={handleEditSopSave}
        onCancel={() => setEditSopModalOpen(false)}
      />

      {/* Edit Priority modal */}
      <EditPriorityModal
        open={editPriorityModalOpen}
        noc={editPriorityNoc}
        theme={theme}
        onSaved={() => {
          natureOfCallListCacheRef.current = null;
          setEditPriorityModalOpen(false);
          setEditPriorityNoc(null);
          refreshManageNocList();
        }}
        onCancel={() => {
          setEditPriorityModalOpen(false);
          setEditPriorityNoc(null);
        }}
      />

      {/* Delete NOC confirmation modal */}
      <ConfirmModal
        open={deleteNocConfirmOpen}
        title="Delete Nature of Call"
        message={deleteNocConfirmMessage}
        confirmText="Delete"
        theme={theme}
        onConfirm={handleConfirmDeleteNocs}
        onCancel={handleCancelDeleteNocs}
      />

      {/* Delete Self Initiate confirmation modal */}
      <ConfirmModal
        open={deleteSelfInitiateConfirmOpen}
        title="Remove from Self Initiate"
        message={deleteSelfInitiateConfirmMessage}
        confirmText="Remove"
        confirmDisabled={isDeletingSelfInitiate}
        busy={isDeletingSelfInitiate}
        theme={theme}
        onConfirm={handleConfirmDeleteSelfInitiateNocs}
        onCancel={handleCancelDeleteSelfInitiateNocs}
      />

      <div className="kst-page-container">
        {/* Mobile Tab Navigation */}
        <div className="kst-mobile-nav">
          <AdminTabBar tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
        </div>

        <div className="kst-layout">
          {/* Desktop Sidebar Navigation */}
          <div className="kst-sidebar">
            <ul className="kst-sidebar-list">
              <li
                className={`kst-sidebar-item${activeTab === 'showManageNocTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={() => handleTabChange('showManageNocTab')}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 2C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2H6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 13H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M8 17H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span className="kst-sidebar-text">NOC</span>
              </li>
              <li
                className={`kst-sidebar-item${activeTab === 'showCustomizeSelfInitiateTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={() => handleTabChange('showCustomizeSelfInitiateTab')}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 18H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M9 6H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span className="kst-sidebar-text">Self Initiate</span>
              </li>
            </ul>
          </div>

          {/* Main Content Area */}
          <div className="kst-main-content">
            {/* NOC Management Page */}
            {activePage === 'showManageNoc' && (
              <div className="kst-page" ref={nocGridContainerRef}>
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Nature of Call Management</h3>
                  <p className="kst-page-description">
                    Update Nature of Call items displayed on Calls for Service and Incident Nature on Case Reports.<br />
                    Internal Investigation does not appear on Calls for Service.<br />
                    Disable a NOC to hide it from selection but still allow to search for it in Records Search and Activity Reports. Delete removes the NOC entirely.
                  </p>
                </div>

                <KstDataGrid
                  columns={nocManagementColumns}
                  rows={nocRows}
                  headerbuttons={nocManagementHeaderButtons}
                  loading={isNocLoading}
                  showcheckbox={true}
                  showordercolumn={true}
                  showstatustoggle={true}
                  mobiletitlefield="value"
                  onButtonCommand={handleNocButtonCommand}
                  onRowMoved={handleNocRowMoved}
                  onStatusSwitch={handleStatusSwitch}
                />
              </div>
            )}

            {/* Customize Self Initiate Page */}
            {activePage === 'showCustomizeSelfInitiate' && (
              <div className="kst-page">
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Customize Self Initiate</h3>
                  <p className="kst-page-description">
                    Customize the buttons that show up on the Self Initiate screen in the MOBILE module. Buttons will show to officers in alphabetical order.<br />
                    You may choose whether or not buttons show for small screen sizes. All items added will show up for large screen sizes.<br />
                    The buttons are for quick access. There is also a dropdown menu on the Self Initiate screen that has all of your Nature of Calls.
                  </p>
                </div>

                <KstDataGrid
                  columns={selfInitiateColumns}
                  rows={selfInitiateRows}
                  headerbuttons={selfInitiateHeaderButtons}
                  loading={isSelfInitiateLoading}
                  showcheckbox={true}
                  mobiletitlefield="value"
                  onButtonCommand={handleSelfInitiateButtonCommand}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

(window as any).NocManagementScreenReact = NocManagementScreen;

